// Enviar una factura al cliente en un clic (pestaña Facturación y cobros).
//
// Al abrirse pide al servidor el correo propuesto (GET /facturas/{id}/envio-cliente):
// destinatario (contacto de facturación del presupuesto o del cliente), copia a
// administracion@, asunto "Factura DCxxxx · evento dd/mm" y el texto estándar.
// Un solo botón "Enviar" (POST /facturas/{id}/enviar-cliente): sale desde
// administracion@ con la marca Duetto y queda registrado ("Enviada el dd/mm a X").
//
// Props: factura ({facturaId, numero}) o null para cerrado; onCerrar(); onEnviada(respuesta)
import React, { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { facturasAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';

const claseInput = 'w-full rounded-lg border border-[#CBD3CF] px-3 py-2 text-sm text-[#16221D] focus:outline-none focus:border-duetto-primary';

const fechaCorta = (iso) => {
  const t = String(iso || '');
  const d = new Date(t);
  if (!t || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

const EnviarFacturaCliente = ({ factura, onCerrar, onEnviada }) => {
  const [datos, setDatos] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const facturaId = factura?.facturaId || null;

  useEffect(() => {
    let vivo = true;
    setDatos(null);
    setForm(null);
    setError(null);
    if (!facturaId) return undefined;
    facturasAPI.getEnvioCliente(facturaId)
      .then(({ data }) => {
        if (!vivo) return;
        setDatos(data);
        setForm({
          para: data?.para || '',
          cc: (data?.cc || []).join(', '),
          asunto: data?.asunto || '',
          cuerpo: data?.cuerpo || '',
        });
      })
      .catch((err) => { if (vivo) setError(err?.response?.data?.detail || err.message || 'No se ha podido preparar el correo'); });
    return () => { vivo = false; };
  }, [facturaId]);

  const cambiar = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));

  const enviar = async () => {
    if (!form || enviando) return;
    if (!form.para.trim() || !form.asunto.trim() || !form.cuerpo.trim()) {
      setError('Faltan el destinatario, el asunto o el texto del correo.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const { data } = await facturasAPI.enviarCliente(facturaId, {
        para: form.para.trim(),
        cc: form.cc.split(',').map(e => e.trim()).filter(Boolean),
        asunto: form.asunto,
        cuerpo: form.cuerpo,
      });
      toast({ title: 'Factura enviada', description: `Enviada a ${data?.enviada_a || form.para.trim()} desde administracion@duettocatering.com` });
      if (onEnviada) onEnviada(data);
      if (onCerrar) onCerrar();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'No se ha podido enviar');
    } finally {
      setEnviando(false);
    }
  };

  const ultimo = datos?.ultimo_envio;
  return (
    <Dialog open={!!facturaId} onOpenChange={(abierto) => { if (!abierto && !enviando && onCerrar) onCerrar(); }}>
      {facturaId && (
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[92vh] overflow-y-auto bg-white p-5 sm:p-6 gap-3" data-testid="modal-enviar-factura">
          <div className="pr-8">
            <DialogTitle className="text-lg font-extrabold text-[#16221D]">
              Enviar la factura {factura?.numero || datos?.numero_factura || ''} al cliente
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5B6B64] mt-1">
              Sale desde administracion@duettocatering.com con el PDF de la factura.
              {ultimo ? ` Ya se envió el ${fechaCorta(ultimo.fecha)} a ${ultimo.para}.` : ''}
            </DialogDescription>
          </div>
          {!form && !error && <p className="text-sm text-[#5B6B64]">Preparando el correo...</p>}
          {form && (
            <>
              <label className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
                Para
                <input type="email" value={form.para} onChange={cambiar('para')} className={claseInput} data-testid="enviar-factura-para" />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
                Copia (separados por comas)
                <input type="text" value={form.cc} onChange={cambiar('cc')} className={claseInput} data-testid="enviar-factura-cc" />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
                Asunto
                <input type="text" value={form.asunto} onChange={cambiar('asunto')} className={claseInput} data-testid="enviar-factura-asunto" />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
                Texto del correo
                <textarea value={form.cuerpo} onChange={cambiar('cuerpo')} rows={11} className={`${claseInput} font-normal`} data-testid="enviar-factura-cuerpo" />
              </label>
            </>
          )}
          {error && (
            <p className="text-sm text-[#8A1C12] bg-[#FDE8E6] border border-[#F5C2BC] rounded-lg px-3 py-2" role="alert">{error}</p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#E1E6E3] pt-3">
            <button
              type="button"
              onClick={() => onCerrar && onCerrar()}
              disabled={enviando}
              className="h-[34px] px-3 rounded-lg border border-[#CBD3CF] bg-white text-[13px] font-bold text-[#16221D] hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={enviar}
              disabled={enviando || !form}
              className="h-[34px] px-3 rounded-lg border border-duetto-primary bg-duetto-primary text-white text-[13px] font-bold flex items-center gap-1.5 hover:bg-opacity-90 disabled:opacity-50"
              data-testid="enviar-factura-enviar"
            >
              <Send className="w-3.5 h-3.5" />
              {enviando ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default EnviarFacturaCliente;
